import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  MoveVertical,
  Sparkles,
  Strikethrough,
  Underline,
} from 'lucide-react';
import clsx from 'clsx';

import { TEXT_UI_ATTR } from '../../elements/text/TextEditOverlay';
import ColorSwatch from '../color-swatch/ColorSwatch';
import EffectsMenu from './EffectsMenu';
import FontPicker from './FontPicker';
import FontSizeInput from './FontSizeInput';
import SpacingMenu from './SpacingMenu';
import { useMediaStudio } from '../../context/MediaStudioContext';

import scss from './TextToolbar.module.scss';

const ALIGN_ORDER = ['left', 'center', 'right'];
const ALIGN_ICONS = { left: AlignLeft, center: AlignCenter, right: AlignRight };
const ALIGN_LABEL_KEYS = { left: 'alignLeft', center: 'alignTextCenter', right: 'alignRight' };

const STYLE_TOGGLES = [
  { key: 'bold', icon: Bold, labelKey: 'bold' },
  { key: 'italic', icon: Italic, labelKey: 'italic' },
  { key: 'underline', icon: Underline, labelKey: 'underline' },
  { key: 'strike', icon: Strikethrough, labelKey: 'strikethrough' },
];

const keepEditorFocus = (event) => event.preventDefault();

function TextToolbar() {
  const { t } = useTranslation('mediaStudio');
  const { textEditing: text, notice } = useMediaStudio();
  const rootRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);
  const { textElement: element, currentStyle: style, hasRange } = text;

  useEffect(() => {
    if (!openMenu) {
      return undefined;
    }
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !event.target.closest?.(`[${TEXT_UI_ATTR}]`)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [openMenu]);

  const toggleMenu = (id) => setOpenMenu((current) => (current === id ? null : id));

  const { align } = element;
  const AlignIcon = ALIGN_ICONS[align] || AlignLeft;
  const nextAlign = align ? ALIGN_ORDER[(ALIGN_ORDER.indexOf(align) + 1) % ALIGN_ORDER.length] : ALIGN_ORDER[0];
  const alignLabel = text.isMixed(align) ? t('mixed') : t(ALIGN_LABEL_KEYS[align]);

  return (
    <div ref={rootRef} className={scss.toolbar} role='toolbar' aria-label={t('typography')} {...{ [TEXT_UI_ATTR]: '' }}>
      <ColorSwatch
        value={style.fill}
        title={t('textColor')}
        onChange={(fill) => text.applyStyle({ fill }, { live: true })}
        onDone={text.endGesture}
      />

      <FontPicker
        value={style.fontFamily}
        open={openMenu === 'font'}
        onToggle={() => toggleMenu('font')}
        onSelect={(fontFamily) => {
          text.applyStyle({ fontFamily });
          setOpenMenu(null);
        }}
        onNotice={notice.show}
      />

      <FontSizeInput
        value={style.fontSize}
        onChange={(fontSize) => text.applyStyle({ fontSize })}
        onStep={
          text.isGroup ? (resize) => text.applyStyle((current) => ({ fontSize: resize(current.fontSize) })) : undefined
        }
      />

      <span className={scss.divider} />

      {STYLE_TOGGLES.map(({ key, icon: Icon, labelKey }) => (
        <button
          key={key}
          type='button'
          className={clsx(scss.iconButton, style[key] && scss.active)}
          title={t(labelKey)}
          aria-label={t(labelKey)}
          aria-pressed={Boolean(style[key])}
          onMouseDown={keepEditorFocus}
          onClick={() => text.toggleStyle(key)}>
          <Icon size={16} />
        </button>
      ))}

      <span className={scss.divider} />

      <button
        type='button'
        className={scss.iconButton}
        title={alignLabel}
        aria-label={alignLabel}
        onMouseDown={keepEditorFocus}
        onClick={() => text.updateBlock({ align: nextAlign })}>
        <AlignIcon size={16} />
      </button>

      <div className={scss.menuAnchor}>
        <button
          type='button'
          className={clsx(scss.iconButton, openMenu === 'spacing' && scss.active)}
          title={t('spacing')}
          aria-label={t('spacing')}
          aria-expanded={openMenu === 'spacing'}
          onMouseDown={keepEditorFocus}
          onClick={() => toggleMenu('spacing')}>
          <MoveVertical size={16} />
        </button>
        {openMenu === 'spacing' ? <SpacingMenu element={element} text={text} /> : null}
      </div>

      <div className={scss.menuAnchor}>
        <button
          type='button'
          className={clsx(scss.textButton, openMenu === 'effects' && scss.active)}
          aria-expanded={openMenu === 'effects'}
          onMouseDown={keepEditorFocus}
          onClick={() => toggleMenu('effects')}>
          <Sparkles size={16} />
          {t('effects')}
        </button>
        {openMenu === 'effects' ? <EffectsMenu element={element} text={text} /> : null}
      </div>

      {hasRange ? <span className={scss.scopeHint}>{t('styleSelection')}</span> : null}
    </div>
  );
}

export default TextToolbar;
